import { useMutation } from "@tanstack/react-query";
import {
  useImperativeHandle,
  useRef,
  useState,
  type ChangeEvent,
  type Ref,
} from "react";
import { HiOutlinePaperClip, HiOutlinePencilSquare } from "react-icons/hi2";

import { isApiClientError } from "../api/errors";
import { uploadConfirmedFile } from "../entities/files";
import { CloseButton } from "./CloseButton";
import { FileRenameDialog } from "./FolderDialogs";
import { formatBytes } from "./formatting";
import type { ShellServices } from "./services";
import { Notice } from "./Notice";

/** Надпись на кнопке: доля показывается, только когда она известна. */
function uploadLabel(pending: boolean, progress: number | null): string {
  if (!pending) return "Добавить файл";
  if (progress === null || progress >= 1) return "Загружаем и проверяем…";
  return `Загружаем… ${Math.round(progress * 100)}%`;
}

/** Выбранное вложение: файл уже лежит в хранилище и проверен. */
export interface PickedAttachment {
  readonly id: string;
  readonly filename: string;
  readonly size: number;
  readonly versionNumber?: number;
  /** Исходная версия с ЭП: сервер отправит её отделённую подпись вместе с файлом. */
  readonly signedVersionId?: string;
}

/**
 * Причина отказа называется своим именем.
 *
 * Прежний текст «файл не прошёл проверку» скрывал главное: чаще всего формат
 * просто не входил в короткий список разрешённых. Хранилище больше не
 * отказывает по формату, и вводить человека в заблуждение незачем.
 */
function uploadMessage(error: unknown): string {
  if (isApiClientError(error)) {
    // Не про права: их достаточно. Неизвестно другое — чьим будет файл.
    if (error.code === "scope.not_selected")
      return "Выберите организацию в шапке: вложение должно кому-то принадлежать.";
    if (error.status === 403)
      return "Недостаточно прав, чтобы загружать файлы в этой организации.";
    if (error.status === 413) return "Файл больше, чем принимает хранилище.";
    if (error.status === 422)
      return "Файл не принят: он исполняемый либо повреждён при передаче. Повторите загрузку.";
    if (error.status === 409)
      return "Загрузка не завершилась: срок ссылки истёк. Повторите попытку.";
    if (error.status === 503)
      return "Хранилище временно недоступно. Повторите попытку позже.";
  }
  return "Не удалось загрузить файл. Проверьте подключение и повторите попытку.";
}

/** Внешний способ добавить файлы: вставка из буфера и перетаскивание. */
export interface AttachmentPickerHandle {
  addFiles: (files: readonly File[]) => void;
}

/**
 * Выбор вложений до сохранения документа.
 *
 * Байты уходят в хранилище сразу, подписанной ссылкой, минуя приложение. К
 * документу при сохранении передаются только идентификаторы уже проверенных
 * файлов, поэтому ошибочно выбранное можно убрать из списка без следов.
 */
export function AttachmentPicker({
  services,
  attachments,
  onChange,
  accessScopeId,
  disabled = false,
  compact = false,
  allowRename = false,
  ref,
}: {
  services: ShellServices;
  attachments: readonly PickedAttachment[];
  onChange: (next: readonly PickedAttachment[]) => void;
  /** Контур документа при работе из сводного списка «Все организации». */
  accessScopeId?: string;
  disabled?: boolean;
  /** Компактный вариант для composer внутри уже озаглавленной секции. */
  compact?: boolean;
  /** Только для потоков, которые сохраняют имя вложения вместе с идентификатором. */
  allowRename?: boolean;
  ref?: Ref<AttachmentPickerHandle>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<PickedAttachment | null>(null);
  // Крупное вложение грузится минутами: без доли выполненного кнопка выглядит
  // зависшей, и человек уходит с середины загрузки.
  const [progress, setProgress] = useState<number | null>(null);

  // Файлы приходят по одному и по несколько за раз: список берём из ref, а не
  // из замыкания, иначе быстрые загрузки затёрли бы друг друга.
  const latest = useRef(attachments);
  latest.current = attachments;

  const upload = useMutation({
    mutationFn: (file: File) =>
      uploadConfirmedFile(services.files, file, {
        accessScopeId,
        onProgress: setProgress,
      }),
    onSuccess: (picked) => {
      setProgress(null);
      setNotice(null);
      latest.current = [...latest.current, picked];
      onChange(latest.current);
    },
    onError: (error) => {
      setProgress(null);
      setNotice(uploadMessage(error));
    },
  });

  const addFiles = (files: readonly File[]) => {
    if (disabled) return;
    for (const file of files) upload.mutate(file);
  };
  useImperativeHandle(ref, () => ({ addFiles }));

  const select = (event: ChangeEvent<HTMLInputElement>) => {
    const files = [...(event.target.files ?? [])];
    event.target.value = "";
    addFiles(files);
  };

  return (
    <section
      className={`attachment-picker${compact ? " attachment-picker--compact" : ""}`}
      aria-labelledby="attachment-picker-title"
    >
      <div className="attachment-picker__head">
        {compact ? (
          <strong id="attachment-picker-title" className="visually-hidden">
            Вложения
          </strong>
        ) : (
          <h2 id="attachment-picker-title">Вложения</h2>
        )}
        <button
          className="button button--secondary button--compact"
          type="button"
          disabled={disabled || upload.isPending}
          onClick={() => inputRef.current?.click()}
        >
          {uploadLabel(upload.isPending, progress)}
        </button>
        <input
          ref={inputRef}
          className="visually-hidden"
          aria-label="Добавить вложение"
          type="file"
          multiple
          onChange={select}
        />
      </div>
      {notice ? <Notice>{notice}</Notice> : null}
      {attachments.length === 0 && !compact ? (
        <p className="dw-secondary">
          Файлы не выбраны. Их можно добавить и после сохранения документа.
        </p>
      ) : attachments.length ? (
        <ul className="attachment-list attachment-picker__list">
          {attachments.map((item) => (
            <li className="attachment-picker__item" key={item.id}>
              <HiOutlinePaperClip
                className="attachment-picker__file-icon"
                aria-hidden="true"
              />
              <span className="attachment-picker__file-copy">
                <strong>{item.filename}</strong>
                <small className="dw-secondary">
                  {item.versionNumber ? `Версия ${item.versionNumber} · ` : ""}
                  {formatBytes(item.size)}
                </small>
              </span>
              <span className="attachment-picker__actions">
                {allowRename ? (
                  <button
                    className="button button--text button--compact file-rename-control"
                    type="button"
                    aria-label={`Переименовать файл ${item.filename}`}
                    title="Переименовать файл"
                    disabled={disabled}
                    onClick={() => setRenaming(item)}
                  >
                    <HiOutlinePencilSquare aria-hidden="true" />
                  </button>
                ) : null}
                <CloseButton
                  size="sm"
                  tone="ghost"
                  disabled={disabled}
                  label={`Убрать файл ${item.filename}`}
                  onClick={() =>
                    onChange(
                      attachments.filter(
                        (candidate) => candidate.id !== item.id,
                      ),
                    )
                  }
                />
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {renaming ? (
        <FileRenameDialog
          filename={renaming.filename}
          onClose={() => setRenaming(null)}
          onSubmit={(filename) => {
            if (disabled) return;
            latest.current = latest.current.map((item) =>
              item.id === renaming.id ? { ...item, filename } : item,
            );
            onChange(latest.current);
            setRenaming(null);
          }}
        />
      ) : null}
    </section>
  );
}
